import React, { useEffect, useMemo, useState } from 'react';
import {
  Bell,
  Pill,
  Stethoscope,
  Clock3,
  CalendarCheck2,
  WalletCards,
  FlaskConical,
  ShoppingBag,
  Truck,
  RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Services from '../lib/services';
import { useNotifications } from '../contexts/NotificationContext';
import FeatureHeader from '../components/FeatureHeader';
import { fetchAllConsultationsWithDoctors } from '../utils/appointments';
import { getRouteFromNotificationData } from '../lib/notificationRouting';
import {
  isReminderRead,
  markReminderRead,
  markAllRemindersRead,
  removeReminderReadState,
} from '../lib/notificationReadState';

export default function NotificationPage() {
  const navigate = useNavigate();
  const {
    notifications: inAppNotifications,
    markRead,
    markAllRead,
    removeNotification,
  } = useNotifications();

  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [sortOrder, setSortOrder] = useState('recent');

  const [medicationReminders, setMedicationReminders] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [labBookings, setLabBookings] = useState([]);

  const userId = useMemo(() => {
    const byId = localStorage.getItem('userId');
    if (byId) return byId;
    try {
      return JSON.parse(localStorage.getItem('user') || '{}')?._id || '';
    } catch {
      return '';
    }
  }, []);

  const readStateKey = useMemo(() => `qureo.notificationPage.read.${userId || 'anon'}`, [userId]);
  const dismissedStateKey = useMemo(() => `qureo.notificationPage.dismissed.${userId || 'anon'}`, [userId]);

  const [localReadIds, setLocalReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(readStateKey) || '[]');
    } catch {
      return [];
    }
  });
  const [localDismissedIds, setLocalDismissedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(dismissedStateKey) || '[]');
    } catch {
      return [];
    }
  });

  const toTimestamp = (value) => {
    const d = new Date(value || 0);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const formatTimeLabel = (value) => {
    const ts = toTimestamp(value);
    if (!ts) return 'Just now';
    const diffMs = Date.now() - ts;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  const markLocalRead = (id) => {
    setLocalReadIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const dismissLocal = (id) => {
    setLocalDismissedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  useEffect(() => {
    try {
      localStorage.setItem(readStateKey, JSON.stringify(localReadIds));
    } catch {
      // ignore persistence failures
    }
  }, [localReadIds, readStateKey]);

  useEffect(() => {
    try {
      localStorage.setItem(dismissedStateKey, JSON.stringify(localDismissedIds));
    } catch {
      // ignore persistence failures
    }
  }, [dismissedStateKey, localDismissedIds]);

  const openNotificationTarget = (item) => {
    if (typeof item?.onOpen === 'function') {
      item.onOpen();
    }
  };

  const renderDomainIcon = (iconKey) => {
    const common = { size: 16, color: 'var(--primary)', strokeWidth: 2 };
    switch (iconKey) {
      case 'medication':
        return <Pill {...common} />;
      case 'consultation_started':
      case 'consultation_booked':
      case 'consultation_30min':
      case 'consultation_reminder':
        return <Stethoscope {...common} />;
      case 'consultation_soon':
        return <Clock3 {...common} />;
      case 'wallet':
        return <WalletCards {...common} />;
      case 'booking':
        return <FlaskConical {...common} />;
      case 'order_placed':
        return <ShoppingBag {...common} />;
      case 'order_delivery':
        return <Truck {...common} />;
      default:
        return <Bell {...common} />;
    }
  };

  const fetchPageData = async () => {
    try {
      setError('');
      setLoading(true);

      const [remindersRes, consultationsRes, walletRes, labBookingRes] = await Promise.allSettled([
        api.get('/api/reminders'),
        userId ? fetchAllConsultationsWithDoctors(userId) : Promise.resolve([]),
        Services.wallet.getTransactions({ limit: 30 }),
        userId ? api.get(`/api/lab-bookings/user/${userId}`) : Promise.resolve({ data: { bookings: [] } }),
      ]);

      const reminderList = remindersRes.status === 'fulfilled'
        ? Array.isArray(remindersRes.value?.data?.reminders)
          ? remindersRes.value.data.reminders
          : []
        : [];

      const medOnly = reminderList
        .filter((item) => `${item?.type || ''}`.toLowerCase() === 'medication')
        .map((item) => ({
          id: `med-${item.id}`,
          reminderId: item.id,
          title: item.title || 'Medication Reminder',
          message: item.message || '',
          createdAt: item.createdAt || item.time,
          read: isReminderRead(item.id),
          raw: item,
        }));
      setMedicationReminders(medOnly);

      setConsultations(
        consultationsRes.status === 'fulfilled' && Array.isArray(consultationsRes.value)
          ? consultationsRes.value
          : []
      );

      const txList = walletRes.status === 'fulfilled'
        ? Array.isArray(walletRes.value?.transactions)
          ? walletRes.value.transactions
          : Array.isArray(walletRes.value?.data?.transactions)
            ? walletRes.value.data.transactions
            : []
        : [];
      setWalletTransactions(txList);

      const bookingList = labBookingRes.status === 'fulfilled'
        ? Array.isArray(labBookingRes.value?.data?.bookings)
          ? labBookingRes.value.data.bookings
          : []
        : [];
      setLabBookings(bookingList);
    } catch (err) {
      console.error('Error loading notification feed:', err);
      setError('Some updates could not be loaded.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const refreshPage = () => {
    if (loading) return;
    setRefreshing(true);
    fetchPageData();
  };

  useEffect(() => {
    fetchPageData();
    const interval = setInterval(fetchPageData, 60000);
    return () => clearInterval(interval);
  }, []);

  const medicationItems = medicationReminders
    .filter((item) => !localDismissedIds.includes(item.id))
    .map((item) => ({
      id: item.id,
      domain: 'medication',
      title: item.title,
      message: item.message,
      timestamp: toTimestamp(item.createdAt),
      timeLabel: formatTimeLabel(item.createdAt),
      label: 'Medication Reminder',
      iconKey: 'medication',
      read: item.read,
      onMarkRead: async () => {
        markReminderRead(item.reminderId);
        setMedicationReminders((prev) => prev.map((p) => (p.id === item.id ? { ...p, read: true } : p)));
      },
      onOpen: () => navigate('/epharmacy'),
      onDismiss: async () => {
        try {
          await api.post('/api/reminders/dismiss', {
            reminderId: item.reminderId,
            type: 'medication',
          });
        } catch {
          // no-op
        }
        removeReminderReadState(item.reminderId);
        setMedicationReminders((prev) => prev.filter((p) => p.id !== item.id));
      },
    }));

  const consultationItems = consultations
    .map((c) => {
      const appointmentTs = toTimestamp(c?.appointmentTime || c?.createdAt);
      if (!appointmentTs) return null;

      const status = `${c?.status || ''}`.toLowerCase();
      const doctorName = c?.doctor?.name || c?.doctor_?.name || 'Doctor';
      const roomId = c?.roomId || c?.consultationRoomId || c?.room;
      const diffMs = appointmentTs - Date.now();

      if (status === 'ongoing') {
        const id = `consult-start-${c._id || appointmentTs}`;
        return {
          id,
          domain: 'timing',
          title: 'Consultation Started',
          message: `Your consultation with ${doctorName} has started${roomId ? ` (Room: ${roomId})` : ''}.`,
          timestamp: appointmentTs,
          timeLabel: formatTimeLabel(appointmentTs),
          label: 'Consultation',
          iconKey: 'consultation_started',
          read: localReadIds.includes(id),
          onMarkRead: () => markLocalRead(id),
          onDismiss: () => dismissLocal(id),
          onOpen: () => navigate(roomId ? `/consultation?room=${roomId}` : '/doctor-consult'),
        };
      }

      if (status === 'scheduled' && diffMs >= 0 && diffMs <= 15 * 60 * 1000) {
        const id = `consult-15-${c._id || appointmentTs}`;
        return {
          id,
          domain: 'timing',
          title: 'Consultation in 15 Minutes',
          message: `Your consultation with ${doctorName} starts soon${roomId ? ` (Room: ${roomId})` : ''}.`,
          timestamp: appointmentTs,
          timeLabel: formatTimeLabel(appointmentTs),
          label: 'Consultation Reminder',
          iconKey: 'consultation_soon',
          read: localReadIds.includes(id),
          onMarkRead: () => markLocalRead(id),
          onDismiss: () => dismissLocal(id),
          onOpen: () => navigate(roomId ? `/consultation?room=${roomId}` : '/doctor-consult'),
        };
      }

      if (status === 'scheduled') {
        const id = `consult-booked-${c._id || appointmentTs}`;
        return {
          id,
          domain: 'consultations',
          title: 'Consultation Booked',
          message: `Consultation with ${doctorName} is scheduled for ${new Date(appointmentTs).toLocaleString()}.`,
          timestamp: appointmentTs,
          timeLabel: formatTimeLabel(appointmentTs),
          label: 'Consultation',
          iconKey: 'consultation_booked',
          read: localReadIds.includes(id),
          onMarkRead: () => markLocalRead(id),
          onDismiss: () => dismissLocal(id),
          onOpen: () => navigate('/appoint'),
        };
      }

      return null;
    })
    .filter(Boolean)
    .filter((item) => !localDismissedIds.includes(item.id));

  const mapInAppDomain = (type = '') => {
    const normalized = String(type).toLowerCase();
    if (normalized.startsWith('wallet_')) return 'wallet';
    if (normalized.startsWith('lab_') || normalized.includes('booking')) return 'bookings';
    if (normalized === 'medication_reminder' || normalized === 'hydration_reminder' || normalized === 'care_plan_step') return 'medication';
    if (normalized.startsWith('consultation_') || normalized.startsWith('in_person_')) return 'timing';
    if (normalized.startsWith('order_') || normalized.startsWith('cart_')) return 'activity';
    return 'activity';
  };

  const mapInAppIcon = (type = '') => {
    const normalized = String(type).toLowerCase();
    if (normalized.startsWith('wallet_')) return 'wallet';
    if (normalized.startsWith('lab_') || normalized.includes('booking')) return 'booking';
    if (normalized === 'medication_reminder' || normalized === 'hydration_reminder' || normalized === 'care_plan_step') return 'medication';
    if (normalized.startsWith('consultation_') || normalized.startsWith('in_person_')) return 'consultation_reminder';
    if (normalized === 'order_delivery') return 'order_delivery';
    if (normalized === 'order_placed') return 'order_placed';
    return 'consultation_reminder';
  };

  const inAppItems = inAppNotifications
    .map((n) => ({
      id: n.id,
      domain: mapInAppDomain(n?.type),
      title: n.title || 'Notification',
      message: n.body || 'You have a new update',
      timestamp: toTimestamp(n.timestamp),
      timeLabel: formatTimeLabel(n.timestamp),
      label: n?.type ? String(n.type).replace(/_/g, ' ') : 'Update',
      iconKey: mapInAppIcon(n?.type),
      read: !!n.read,
      onMarkRead: () => markRead(n.id),
      onDismiss: () => removeNotification(n.id),
      onOpen: () => {
        navigate(getRouteFromNotificationData({ ...n.data, type: n.type }));
      },
    }));

  const walletItems = walletTransactions
    .map((tx, idx) => {
      const id = `wallet-${tx._id || tx.id || idx}`;
      const type = `${tx?.type || ''}`.toLowerCase();
      const amount = Number(tx?.amount || 0);
      const amountLabel = Number.isFinite(amount) ? `$${amount.toFixed(2)}` : 'Amount unavailable';
      let title = 'Wallet Transaction';
      if (type.includes('deposit')) title = 'Wallet Deposit';
      if (type.includes('withdraw')) title = 'Wallet Withdrawal';
      if (type.includes('payment')) title = 'Wallet Payment';
      if (type.includes('refund')) title = 'Wallet Refund';

      return {
        id,
        domain: 'wallet',
        title,
        message: `${tx?.serviceDetails || tx?.description || 'Transaction recorded'} • ${amountLabel}`,
        timestamp: toTimestamp(tx?.createdAt || tx?.date),
        timeLabel: formatTimeLabel(tx?.createdAt || tx?.date),
        label: 'Wallet Transaction',
        iconKey: 'wallet',
        read: localReadIds.includes(id),
        onMarkRead: () => markLocalRead(id),
        onDismiss: () => dismissLocal(id),
        onOpen: () => navigate('/health-wallet'),
      };
    })
    .filter((item) => !localDismissedIds.includes(item.id));

  const bookingItems = labBookings
    .map((booking, idx) => {
      const id = `booking-${booking._id || idx}`;
      const pickupDate = booking?.bookingDate || booking?.date || booking?.createdAt;
      return {
        id,
        domain: 'bookings',
        title: 'Booking Confirmed',
        message: `Lab booking scheduled${pickupDate ? ` for ${new Date(pickupDate).toLocaleString()}` : ''}.`,
        timestamp: toTimestamp(booking?.createdAt || booking?.bookingDate),
        timeLabel: formatTimeLabel(booking?.createdAt || booking?.bookingDate),
        label: 'Booking',
        iconKey: 'booking',
        read: localReadIds.includes(id),
        onMarkRead: () => markLocalRead(id),
        onDismiss: () => dismissLocal(id),
        onOpen: () => navigate(booking?._id ? `/booking-details/${booking._id}` : '/lab-tests'),
      };
    })
    .filter((item) => !localDismissedIds.includes(item.id));

  const allItems = [
    ...consultationItems,
    ...inAppItems,
    ...walletItems,
    ...bookingItems,
    ...medicationItems,
  ]
    .sort((a, b) => b.timestamp - a.timestamp);

  const filteredItems = allItems.filter((item) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !item.read;
    return item.domain === filter;
  }).sort((a, b) => sortOrder === 'recent' ? b.timestamp - a.timestamp : a.timestamp - b.timestamp);

  const unreadCount = filteredItems.filter((i) => !i.read).length;

  const handleMarkAllRead = () => {
    const unreadItems = allItems.filter((item) => !item.read);
    if (!unreadItems.length) {
      return;
    }

    const inAppIds = new Set(inAppNotifications.map((n) => n.id));

    const localIdsToMark = unreadItems
      .filter((item) => !item.id.startsWith('med-') && !inAppIds.has(item.id))
      .map((item) => item.id);

    if (localIdsToMark.length) {
      setLocalReadIds((prev) => Array.from(new Set([...prev, ...localIdsToMark])));
    }

    if (unreadItems.some((item) => inAppIds.has(item.id))) {
      markAllRead();
    }

    const medIds = medicationReminders
      .filter((m) => !m.read)
      .map((m) => m.reminderId)
      .filter(Boolean);

    if (medIds.length) {
      markAllRemindersRead(medIds);
      setMedicationReminders((prev) => prev.map((p) => ({ ...p, read: true })));
    }
  };

  return (
    <div style={pageStyle}>
      <div style={contentWrapStyle}>
        <div style={styles.header}>
          <FeatureHeader
            title="Notifications"
            subtitle="Consultations, wallet transactions, bookings and medication updates"
            onBack={() => navigate(-1)}
            subtitleStyle={{ color: 'var(--on-bg)', fontWeight: 500 }}
            containerStyle={{ padding: 0 }}
          />
        </div>

        <div style={styles.filterRow}>
          {[
            { id: 'all', label: 'All' },
            { id: 'unread', label: 'Unread' },
            { id: 'consultations', label: 'Consultations' },
            { id: 'wallet', label: 'Wallet' },
            { id: 'bookings', label: 'Bookings' },
            { id: 'medication', label: 'Medication' },
            { id: 'timing', label: 'Timing' },
            { id: 'activity', label: 'Activity' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                ...styles.filterChip,
                ...(filter === f.id ? styles.filterChipActive : {}),
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={styles.bulkBar}>
          <div style={styles.bulkBarLeft}>
            <span style={styles.bulkIconWrap}>○</span>
            <span style={styles.bulkText}>Mark all as read</span>
          </div>
          <button onClick={handleMarkAllRead} style={styles.bulkAction}>
            Mark all
          </button>
        </div>

        <div style={styles.sectionHeadRow}>
          <div>
            <div style={styles.sectionTitle}>Updates</div>
            <div style={styles.sectionSubtitle}>{loading ? 'Loading updates...' : `You have ${unreadCount} unread updates`}</div>
          </div>
          <button
            style={styles.sortButton}
            type="button"
            onClick={() => setSortOrder((current) => current === 'recent' ? 'oldest' : 'recent')}
            aria-label={`Sort by ${sortOrder === 'recent' ? 'oldest' : 'most recent'}`}
          >
            {sortOrder === 'recent' ? 'Most recent' : 'Oldest first'} <span style={styles.sortCaret}>↕</span>
          </button>
        </div>

        <div style={styles.statusRow}>
          {error && <span style={styles.errorText}>{error}</span>}
          <button type="button" style={styles.refreshButton} onClick={refreshPage} disabled={loading}>
            <RefreshCw size={14} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </div>

        {filteredItems.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => openNotificationTarget(item)}
                style={{
                  ...styles.notificationCard,
                  opacity: item.read ? 0.7 : 1,
                }}
              >
                <div style={styles.leftIconWrap}>
                  <span style={styles.leftIcon}>{renderDomainIcon(item.iconKey)}</span>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.categoryLine}>{item.label}</div>
                  <div style={styles.cardTitle}>{item.title}</div>
                  <div style={styles.cardMessage}>{item.message}</div>
                  <div style={styles.cardActions}>
                    {!item.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          item.onMarkRead();
                        }}
                        style={styles.linkAction}
                      >
                        Mark Read
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        item.onDismiss();
                      }}
                      style={styles.linkAction}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>

                <div style={styles.rightMeta}>
                  <div style={styles.timeText}>{item.timeLabel}</div>
                  <button
                    type="button"
                    style={styles.chevronButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      openNotificationTarget(item);
                    }}
                  >
                    ›
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: '#999' }}>
            <Bell size={40} style={{ marginBottom: 16, opacity: 0.3 }} />
            <p>{allItems.length === 0 ? 'No notifications yet' : 'No notifications match this filter'}</p>
            {allItems.length > 0 && filter !== 'all' && (
              <button type="button" style={styles.clearFilterButton} onClick={() => setFilter('all')}>
                View all updates
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  padding: '0 0 24px',
  background: 'var(--bg)',
  fontFamily: "'Poppins', sans-serif",
  color: 'var(--on-bg)',
};

const contentWrapStyle = {
  maxWidth: 640,
  margin: '0 auto',
  padding: '0 16px',
  boxSizing: 'border-box',
};

const styles = {
  header: {
    background: 'transparent',
    borderRadius: 0,
    padding: '18px 0 8px',
    marginBottom: 20,
    boxShadow: 'none',
  },
  headerTop: {
    display: 'flex',
    marginTop: 0,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backArrow: {
    cursor: 'pointer',
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--primary)',
    marginTop: 4,
    marginRight: 0,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
  headerTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 22,
    marginLeft: 12,
    color: 'var(--on-bg)',
    marginBottom: 0,
    letterSpacing: '0.3px',
    textAlign: 'center',
    lineHeight: 1.15,
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'var(--on-bg)',
    fontWeight: 500,
    marginBottom: 0,
    marginTop: 4,
    marginLeft: 34,
    textAlign: 'left',
  },
  filterRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 14,
    flexWrap: 'nowrap',
    overflowX: 'auto',
    paddingBottom: 4,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  filterChip: {
    flex: '0 0 auto',
    padding: '8px 16px',
    borderRadius: 999,
    border: '1px solid color-mix(in srgb, var(--primary) 16%, #e5e7eb)',
    background: '#fff',
    color: 'var(--on-bg)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  filterChipActive: {
    background: 'var(--primary)',
    color: '#fff',
    border: '1px solid var(--primary)',
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 14px',
    borderRadius: 14,
    background: '#fff',
    border: '1px solid color-mix(in srgb, var(--primary) 14%, #e5e7eb)',
    boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
    marginBottom: 16,
  },
  bulkBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  bulkIconWrap: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    border: '1px solid color-mix(in srgb, var(--primary) 32%, #e5e7eb)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--primary)',
    fontSize: 14,
    flexShrink: 0,
  },
  bulkText: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--on-bg)',
    whiteSpace: 'nowrap',
  },
  bulkAction: {
    background: 'transparent',
    border: 'none',
    color: 'var(--primary)',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    whiteSpace: 'nowrap',
  },
  sectionHeadRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--on-bg)',
    lineHeight: 1.2,
  },
  sectionSubtitle: {
    marginTop: 4,
    fontSize: 12,
    color: '#6b7280',
  },
  statusRow: {
    minHeight: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
  },
  errorText: {
    color: '#b42318',
    fontSize: 12,
  },
  refreshButton: {
    marginLeft: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: 'none',
    background: 'transparent',
    color: 'var(--primary)',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    padding: '6px 0',
  },
  clearFilterButton: {
    border: 'none',
    background: 'transparent',
    color: 'var(--primary)',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  sortButton: {
    border: '1px solid color-mix(in srgb, var(--primary) 14%, #e5e7eb)',
    background: '#fff',
    borderRadius: 999,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  sortCaret: {
    color: 'var(--primary)',
    fontSize: 12,
  },
  notificationCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px',
    background: '#fff',
    borderRadius: 18,
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 6px rgba(15,23,42,0.04)',
    cursor: 'pointer',
  },
  leftIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '1px solid color-mix(in srgb, var(--primary) 14%, #e5e7eb)',
    background: 'color-mix(in srgb, var(--primary) 8%, #fff)',
  },
  leftIcon: {
    fontSize: 16,
    color: 'var(--primary)',
    fontWeight: 700,
  },
  categoryLine: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--primary)',
    marginBottom: 4,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--on-bg)',
    lineHeight: 1.25,
    marginBottom: 4,
  },
  cardMessage: {
    fontSize: 12,
    lineHeight: 1.35,
    color: '#6b7280',
  },
  rightMeta: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    gap: 10,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  timeText: {
    fontSize: 11,
    color: '#6b7280',
    whiteSpace: 'nowrap',
  },
  chevronButton: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '1px solid color-mix(in srgb, var(--primary) 18%, #e5e7eb)',
    background: '#fff',
    color: 'var(--primary)',
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardActions: {
    display: 'flex',
    gap: 12,
    marginTop: 8,
  },
  linkAction: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: 'var(--primary)',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
